// Shared money formatter (Aug 2026 finance dashboard).
//
// One place, one behaviour — the pre-existing inline `formatCurrency`
// definitions across Student billing / Invoice pages carry subtle
// differences (some round to 0dp, some produce "$0" for null). This
// helper normalises to a single canonical output for every dashboard
// surface. Existing pages are unchanged; they can migrate opt-in.

const NF = new Intl.NumberFormat("en-CA", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Canonical currency formatter. Rules:
 *   null / undefined / NaN → "$0.00"
 *   200                    → "$200.00"
 *   1250.5                 → "$1,250.50"
 *   -50                    → "-$50.00"
 */
export function formatCurrency(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === "") return "$0.00";
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return "$0.00";
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  return `${sign}$${NF.format(abs)}`;
}

/**
 * Compact big-number formatter for large dashboard KPIs. Always
 * paired with a full-value tooltip so nothing is hidden.
 *   142_800 → "$142.8K"
 *   1_500_000 → "$1.5M"
 *   250 → "$250.00"
 */
export function formatCurrencyCompact(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === "") return "$0.00";
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return "$0.00";
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs < 1000) return formatCurrency(n);
  if (abs < 1_000_000) return `${sign}$${(abs / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  return `${sign}$${(abs / 1_000_000).toFixed(2).replace(/\.?0+$/, "")}M`;
}
